import React from 'react';

// A day (or a group) against its usual: a center line for "a usual day", a bar
// growing right in forest for busier or left in dusty rose for slower, and a
// thin whisker for the range. One scale is passed in so every bar on a card is
// drawn to the same ruler. The words beside it carry the value; this is aria-hidden.

export interface Side { bar: number; spacer: number; whisker: number }

const share = (v: number, scale: number) => Math.min(100, Math.max(0, (v / scale) * 100));

/** Each half's bar, the gap before its whisker, and the whisker, as % of that half. */
export function deviationGeometry(point: number, low: number, high: number, scale: number): { left: Side; right: Side } {
  const s = scale > 0 ? scale : 1;
  const [lo, hi] = low <= high ? [low, high] : [high, low];
  const side = (near: number, far: number, bar: number): Side => {
    const spacer = share(near, s);
    return { bar: share(bar, s), spacer, whisker: Math.max(0, share(far, s) - spacer) };
  };
  return {
    left: side(Math.max(-hi, 0), Math.max(-lo, 0), Math.max(-point, 0)),
    right: side(Math.max(lo, 0), Math.max(hi, 0), Math.max(point, 0)),
  };
}

const INK = 'bg-[#2D2A2E]';

function Half({ side, dir, muted, capNear }: { side: Side; dir: 'left' | 'right'; muted: boolean; capNear: boolean }) {
  const toward = dir === 'left' ? 'flex-row-reverse' : 'flex-row';
  const tone = dir === 'left' ? 'bg-[#B54A4A]' : 'bg-[#2E6B4F]';
  // Square at the center line, a 4px round on the data end.
  const end = dir === 'left' ? 'rounded-l-[4px]' : 'rounded-r-[4px]';
  return (
    <div className="relative w-1/2 h-full">
      {side.bar > 0 && (
        <div className={`absolute inset-0 flex ${toward} items-center`}>
          <div data-bar={dir} className={`h-2 ${tone} ${end} ${muted ? 'opacity-40' : ''}`} style={{ width: `${side.bar}%` }} />
        </div>
      )}
      {side.whisker > 0 && (
        <div className={`absolute inset-0 flex ${toward} items-center`}>
          <div style={{ width: `${side.spacer}%` }} />
          <div data-whisker={dir} className="relative h-2" style={{ width: `${side.whisker}%` }}>
            <div className={`absolute inset-x-0 top-1/2 h-px -translate-y-1/2 ${INK} ${muted ? 'opacity-40' : 'opacity-70'}`} />
            <div className={`absolute top-0 bottom-0 w-px ${INK} ${muted ? 'opacity-40' : 'opacity-70'} ${dir === 'left' ? 'left-0' : 'right-0'}`} />
            {capNear && <div className={`absolute top-0 bottom-0 w-px ${INK} ${muted ? 'opacity-40' : 'opacity-70'} ${dir === 'left' ? 'right-0' : 'left-0'}`} />}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DeviationBar({ point, low, high, scale, muted = false, className = '' }: {
  point: number; low: number; high: number; scale: number; muted?: boolean; className?: string;
}) {
  const g = deviationGeometry(point, low, high, scale);
  return (
    <div aria-hidden="true" className={`relative flex h-4 w-full ${className}`}>
      <Half side={g.left} dir="left" muted={muted} capNear={g.right.whisker === 0} />
      <Half side={g.right} dir="right" muted={muted} capNear={g.left.whisker === 0} />
      {/* The center line: a usual day. */}
      <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-[#6B6670]" />
    </div>
  );
}
